import { MoveItemsToContainerInput } from "src/types/toteTypes";
import {
  WadmContainer,
  ContainerUsingFor,
  AddItemToContainerInput,
  ItemFlowContext,
} from "src/types/containerTypes";
import { mapToMoveToteItem } from "src/services/toteService";

const WEIGHT_CONVERSION_FACTORS: Record<string, Record<string, number>> = {
  POUND: { KILOGRAM: 0.45359 },
  KILOGRAM: { POUND: 2.20462 },
};

// WADM uses GRAM as a legacy unit that is numerically equivalent to KILOGRAM.
// We match Austin's behavior.
const normalizeWeightUnit = (unit: string): string =>
  unit.toUpperCase() === "GRAM" ? "KILOGRAM" : unit.toUpperCase();

export function convertWeight(weight: number, itemUnit: string, containerUnit: string): number {
  const sourceUnit = normalizeWeightUnit(itemUnit);
  const targetUnit = normalizeWeightUnit(containerUnit);
  if (sourceUnit === targetUnit) return weight;

  const factor = WEIGHT_CONVERSION_FACTORS[sourceUnit]?.[targetUnit];
  return factor !== undefined ? weight * factor : weight;
}

export interface ConfirmItem {
  id: string;
  imageUrl?: string;
  name: string;
  asin?: string;
  defaultQuantity: string;
  defaultWeight: string;
  weightUnit?: string;
  maxQuantity?: number;
  unitWeight?: number;
}

/**
 * Normalizes the ItemFlowContext into a flat list of ConfirmItems page for rendering.
 * Both tote (multiple items) and classification (single item) flows produce the
 * same shape.
 */
export function normalizeItemsForDisplay(itemFlowContext: ItemFlowContext): ConfirmItem[] {
  if (itemFlowContext.type === "tote") {
    return itemFlowContext.items.map((item) => ({
      id: item.id,
      imageUrl: item.images?.[0]?.url,
      name: item.name ?? "Unknown Product",
      asin: item.asin,
      defaultQuantity: String(item.quantity),
      defaultWeight: (item.quantity * (item.weight?.value ?? 0)).toFixed(3),
      weightUnit: item.weight?.unit,
      maxQuantity: item.quantity,
      unitWeight: item.weight?.value,
    }));
  }

  const pc = itemFlowContext.productClassification;
  return [
    {
      id: pc.asin,
      imageUrl: pc.images?.[0]?.url,
      name: pc.name ?? pc.asin,
      asin: pc.asin,
      defaultQuantity: "1",
      defaultWeight: pc.product_attributes?.weight?.value?.toFixed(3) ?? "",
      weightUnit: pc.product_attributes?.weight?.unit,
      unitWeight: pc.product_attributes?.weight?.value,
    },
  ];
}

// Request build for Tote flow — moves one or more existing tote items into a container.
export function buildMoveItemsRequest(params: {
  itemFlowContext: ItemFlowContext;
  container?: WadmContainer;
  usingFor: ContainerUsingFor;
  quantityOverrides: Record<string, string>;
  weightOverrides: Record<string, string>;
  siteId: string;
  overrideReasons?: Record<string, string>;
}): MoveItemsToContainerInput {
  const { itemFlowContext, container, usingFor, quantityOverrides, weightOverrides, siteId, overrideReasons } = params;

  if (itemFlowContext.type !== "tote")
    throw new Error("buildMoveItemsRequest called with non-tote context");

  const { items, scannableId } = itemFlowContext;

  const moveToteItems = items.map((item) => {
    const qty = parseInt(quantityOverrides[item.id] ?? String(item.quantity), 10) || item.quantity;
    const computedWeight = qty * (item.weight?.value ?? 0);
    const overrideWeight = parseFloat(weightOverrides[item.id] ?? "");
    const finalWeight = isNaN(overrideWeight) ? computedWeight : overrideWeight;
    const overrideDonationReason = overrideReasons?.[item.id];
    return mapToMoveToteItem(item, scannableId, container, qty, finalWeight, overrideDonationReason);
  });

  return {
    siteId,
    wadmContainerId: container?.id ?? "",
    usingFor,
    items: moveToteItems,
  };
}

// Request build for Single item flow — adds a single barcode-scanned item into a container.
export function buildAddItemRequest(params: {
  itemFlowContext: ItemFlowContext;
  container?: WadmContainer;
  quantityOverrides: Record<string, string>;
  weightOverrides: Record<string, string>;
  siteName: string;
  overrideReasons?: Record<string, string>;
}): AddItemToContainerInput {
  const { itemFlowContext, container, quantityOverrides, weightOverrides, siteName, overrideReasons } = params;

  if (itemFlowContext.type !== "classification" && itemFlowContext.type !== "toteExtraItem")
    throw new Error("buildAddItemRequest called with non-classification context");

  if (!container) throw new Error("buildAddItemRequest requires a container");

  const { productClassification: pc } = itemFlowContext;
  const usingFor = container.usingFor;
  const isWaste = usingFor === "WASTE";
  const isDonation = usingFor === "DONATION";

  const quantity = parseInt(quantityOverrides[pc.asin] ?? "1", 10) || 1;
  const unitWeight = pc.product_attributes?.weight?.value ?? 0;
  const parsedWeight = parseFloat(weightOverrides[pc.asin] ?? "");
  const weight = isNaN(parsedWeight) ? quantity * unitWeight : parsedWeight;

  const recommendedProfileIds = pc.waste_profiles?.map((p) => p.id) ?? [];
  const recommendedCategoryIds = pc.donation_categories?.map((d) => d.id) ?? [];

  return {
    containerId: container.id,
    name: pc.name ?? pc.asin ?? "",
    asin: pc.asin,
    quantity,
    totalWeight: {
      weight,
      unit: container.maxWeight?.unit ?? pc.product_attributes?.weight?.unit ?? "POUND",
    },
    profileId: isWaste ? container.wasteProfileId : undefined,
    isProfileOverridden:
      isWaste && container.wasteProfileId
        ? !recommendedProfileIds.includes(container.wasteProfileId)
        : false,
    categoryId: isDonation ? container.donationCategoryId : undefined,
    isCategoryOverridden:
      isDonation && container.donationCategoryId
        ? !recommendedCategoryIds.includes(container.donationCategoryId)
        : false,
    siteName,
    ...(overrideReasons?.[pc.asin] && { overrideDonationReason: overrideReasons[pc.asin] }),
    ...(itemFlowContext.type === "toteExtraItem" && {
      toteContainerId: itemFlowContext.toteContainerId,
      isExtraItemInTote: true,
    }),
  };
}
